import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { aboutContent, firmInfo } from '../data/mockData';
import { CheckCircle, Award, Users, Clock, Shield, Scale, X, Briefcase, GraduationCap, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const [selectedMember, setSelectedMember] = useState(null);

  const stats = [
    { icon: Clock, value: '40+', label: 'Years Experience' },
    { icon: Users, value: '10,000+', label: 'Cases Handled' },
    { icon: Award, value: '$12M', label: 'Largest Settlement' },
    { icon: Shield, value: '100%', label: 'Client Focused' },
  ];

  const team = [
    {
      name: 'Bill Power',
      title: 'Founder & Lead Attorney',
      image: 'https://customer-assets.emergentagent.com/job_attorney-experts/artifacts/ey1irqb4_IMG_0182.jpeg',
      shortBio: 'Founder and lead attorney with years of experience delivering strategic legal solutions.',
      fullBio: `Bill Power is the founder and lead attorney of the firm, bringing years of experience and a strong commitment to delivering strategic legal solutions for his clients. With a deep understanding of complex legal matters, Bill has built a reputation for professionalism, integrity, and results-driven advocacy.

As the firm's principal attorney, Bill oversees all major cases and provides guidance on legal strategy, ensuring that every client receives thorough and personalized representation. His leadership has shaped the firm's dedication to excellence, attention to detail, and client-focused service.

Bill specializes in handling high-value asset matters, estate-related legal processes, and structured financial arrangements. His approach combines legal expertise with practical insight, allowing clients to navigate complex situations with confidence and clarity.

Under his direction, the firm continues to uphold the highest standards of legal practice while maintaining a strong commitment to client trust and confidentiality.`,
      specializations: ['High-Value Asset Matters', 'Estate-Related Legal Processes', 'Structured Financial Arrangements', 'Complex Litigation'],
      education: ['Juris Doctor, Top Law School', 'Bachelor of Arts in Political Science'],
      awards: ['Super Lawyers Recognition', 'Top 100 Trial Lawyers', 'Client Choice Award']
    },
    {
      name: 'Carolina Mitchell',
      title: 'Senior Partner',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=500&fit=crop',
      shortBio: 'With over 25 years of trial experience, Carolina has secured millions in settlements for her clients.',
      fullBio: `Carolina Mitchell is a Senior Partner at Carolina & Bill Law Firm, bringing over 25 years of distinguished trial experience to every case she handles. Her unwavering dedication to her clients and exceptional courtroom skills have resulted in millions of dollars in settlements and verdicts.

Carolina's approach to legal representation is characterized by meticulous preparation, compassionate client care, and aggressive advocacy when needed. She believes that every client deserves to have their story heard and their rights protected.

Throughout her career, Carolina has handled thousands of personal injury cases, from automobile accidents to complex medical malpractice claims. Her track record of success has earned her recognition from peers and clients alike.

Carolina is deeply committed to the Richmond community and regularly volunteers her time to provide legal education and pro bono services to those in need.`,
      specializations: ['Personal Injury Litigation', 'Medical Malpractice', 'Automobile Accidents', 'Wrongful Death Cases'],
      education: ['Juris Doctor, University of Virginia School of Law', 'Bachelor of Science in Psychology'],
      awards: ['Virginia Super Lawyers', 'Best Lawyers in America', 'Martindale-Hubbell AV Rated']
    },
    {
      name: 'Sarah Thompson',
      title: 'Associate Attorney',
      image: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=400&h=500&fit=crop',
      shortBio: 'Sarah specializes in automobile accidents and medical malpractice cases.',
      fullBio: `Sarah Thompson joined Carolina & Bill Law Firm as an Associate Attorney, bringing fresh perspective and dedication to the practice of personal injury law. Her keen analytical skills and compassionate approach make her an invaluable advocate for clients navigating difficult times.

Sarah specializes in automobile accident cases and medical malpractice claims, where her attention to detail and thorough understanding of medical records have proven essential in building strong cases for her clients.

Before joining the firm, Sarah gained valuable experience clerking for a federal judge and working at a prominent healthcare litigation firm. This background gives her unique insight into both the legal and medical aspects of personal injury cases.

Sarah is passionate about ensuring that injury victims receive the compensation they deserve and works tirelessly to achieve the best possible outcomes for her clients.`,
      specializations: ['Automobile Accidents', 'Medical Malpractice', 'Insurance Bad Faith', 'Product Liability'],
      education: ['Juris Doctor, William & Mary Law School', 'Bachelor of Arts in Biology'],
      awards: ['Rising Stars - Super Lawyers', 'Pro Bono Service Award']
    },
    {
      name: 'Michael Rodriguez',
      title: 'Associate Attorney',
      image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=500&fit=crop',
      shortBio: 'Michael focuses on trucking accidents and catastrophic injury cases.',
      fullBio: `Michael Rodriguez is an Associate Attorney at Carolina & Bill Law Firm, where he focuses on trucking accidents and catastrophic injury cases. His determination and thorough approach to case preparation have resulted in significant recoveries for clients who have suffered life-altering injuries.

Michael understands the devastating impact that serious injuries can have on victims and their families. He is committed to holding negligent parties accountable and securing the resources his clients need for medical care, rehabilitation, and future security.

His expertise in trucking accident cases includes knowledge of federal motor carrier regulations, electronic logging devices, and the complex insurance structures that govern the commercial trucking industry.

Michael is bilingual in English and Spanish, allowing him to serve a broader community of clients and ensure clear communication throughout the legal process.`,
      specializations: ['Trucking Accidents', 'Catastrophic Injuries', 'Spinal Cord Injuries', 'Traumatic Brain Injuries'],
      education: ['Juris Doctor, University of Richmond School of Law', 'Bachelor of Arts in Criminal Justice'],
      awards: ['National Trial Lawyers Top 40 Under 40', 'Virginia State Bar Leadership Award']
    },
  ];

  const TeamMemberModal = ({ member, onClose }) => {
    if (!member) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
        <div 
          className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative">
            <div className="h-32 bg-gradient-to-r from-blue-900 to-blue-800"></div>
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="absolute -bottom-16 left-8">
              <img 
                src={member.image} 
                alt={member.name}
                className="w-32 h-32 rounded-xl object-cover border-4 border-white shadow-lg"
              />
            </div>
          </div>

          {/* Content */}
          <div className="pt-20 px-8 pb-8">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900">{member.name}</h2>
              <p className="text-blue-900 font-semibold text-lg">{member.title}</p>
            </div>

            {/* Tabs Content */}
            <div className="space-y-8">
              {/* Bio Section */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-900" />
                  Biography
                </h3>
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {member.fullBio}
                </div>
              </div>

              {/* Specializations */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Scale className="w-5 h-5 text-blue-900" />
                  Areas of Practice
                </h3>
                <div className="flex flex-wrap gap-2">
                  {member.specializations.map((spec, idx) => (
                    <span 
                      key={idx}
                      className="px-4 py-2 bg-blue-50 text-blue-900 rounded-full text-sm font-medium"
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>

              {/* Education */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-blue-900" />
                  Education
                </h3>
                <ul className="space-y-2">
                  {member.education.map((edu, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-600">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      {edu}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Awards */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-900" />
                  Awards & Recognition
                </h3>
                <ul className="space-y-2">
                  {member.awards.map((award, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-600">
                      <Award className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      {award}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact CTA */}
              <div className="pt-4 border-t border-gray-200">
                <Button
                  asChild
                  className="bg-blue-900 hover:bg-blue-800 text-white font-semibold px-6 py-3"
                >
                  <Link to="/contact">
                    <Phone className="w-4 h-4 mr-2" />
                    Schedule a Consultation
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              About <span className="text-blue-900">{firmInfo.name}</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fighting for justice for the injured since 1985
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow">
                <stat.icon className="w-8 h-8 text-blue-900 mx-auto mb-4" />
                <div className="text-3xl font-bold text-blue-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center">
              <Scale className="w-7 h-7 text-amber-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
          </div>
          
          <p className="text-gray-600 text-lg leading-relaxed mb-8">
            {aboutContent.mainText}
          </p>
          
          <blockquote className="text-blue-900 text-xl font-semibold mb-8 border-l-4 border-amber-500 pl-6 py-4 bg-blue-50 rounded-r-lg">
            {aboutContent.highlight}
          </blockquote>

          <p className="text-gray-600 text-lg leading-relaxed mb-12">
            {aboutContent.secondaryText}
          </p>

          {/* Key Points */}
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Over 40 years of trial experience',
              'No fee unless we win your case',
              'We come to you',
              'Handled over 10,000 cases',
              'Fighting for the injured',
              '24/7 Free consultation',
              'Personalized attention',
              'Aggressive representation'
            ].map((point, index) => (
              <div key={index} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-gray-600 text-lg">Click on a team member to learn more about their experience</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div 
                key={index} 
                className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
                onClick={() => setSelectedMember(member)}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                    <span className="text-white font-semibold px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                      View Profile
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-blue-900 transition-colors">{member.name}</h3>
                  <p className="text-blue-900 text-sm font-medium mb-2">{member.title}</p>
                  <p className="text-gray-600 text-sm line-clamp-2">{member.shortBio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Fight for Justice?</h2>
          <p className="text-blue-200 mb-8">Contact us today for a free, no-obligation case review.</p>
          <Button
            asChild
            size="lg"
            className="bg-white hover:bg-gray-100 text-blue-900 font-bold px-8 py-6 text-lg rounded-md"
          >
            <Link to="/contact">Get Free Consultation</Link>
          </Button>
        </div>
      </section>

      {/* Team Member Modal */}
      {selectedMember && (
        <TeamMemberModal member={selectedMember} onClose={() => setSelectedMember(null)} />
      )}

      <Footer />
    </div>
  );
};

export default AboutPage;
